import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import { PopupProvider } from './context/PopupContext';
import { AlertProvider } from './context/AlertContext';
import { LanguageProvider } from './language/LanguageContext';
import AdminLayout from './layouts/AdminLayout';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Users from './pages/Users';
import UserHistory from './pages/UserHistory';
import Feedback from './pages/Feedback';
import Notice from './pages/Notice';
import { setupInterceptors } from './api';
import './index.css';

// Check Role Component
const RequireAdmin = ({ children }) => {
	const { user, loading } = useUser();
	const location = useLocation();

	if (loading) return <div className="flex h-screen items-center justify-center">Loading...</div>;

	if (!user) {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	// Role check - assuming user object has role from backend
	// If role is undefined (old users), treat as user? Or deny?
	// Plan said: default 'user'.
	if (user.role !== 'admin') {
		return (
			<div className="flex h-screen items-center justify-center flex-col">
				<h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
				<p>You do not have administrator privileges.</p>
				<button
					onClick={() => window.location.href = '/login'}
					className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
				>
					Back to Login
				</button>
			</div>
		);
	}

	return children;
};

// Interceptor Setup Component
const InterceptorSetup = () => {
	const { logout } = useUser();
	// navigate is needed for interceptors to redirect on 401
	// But setupInterceptors takes navigate.
	// We can't access navigate inside Router unless we are a child.
	// So this component must be inside Router.
	// However, setupInterceptors is usually global.
	// Let's passed it a custom navigate or just use it once.
	// In the original code, setupInterceptors(navigate) is called.

	// Actually, `api/index.js` exports setupInterceptors.
	// It attaches response interceptor.

	// We need to call it once.
	// But hooks can only be used in component.

	// Wait, original App.js probably called it.
	// Let's try to call it in a useEffect inside a component under Router.

	// ...
	return null;
};

const MainRoutes = () => {
	const navigate = (path) => {
		window.location.href = path; // Simple fallback or use useNavigate logic if adaptable
	};
	// setupInterceptors supports navigate function. 
	// Ideally we pass the real navigate from `useNavigate`.
	// But `setupInterceptors` in `api/index.js` uses it to redirect.
	// Let's try:

	// const nav = useNavigate();
	// useEffect(() => {
	//    setupInterceptors(nav);
	// }, []);

	// But multiple calls might stack interceptors?
	// `api/index.js` has `interceptorsSet` flag to prevent dupes. Good.

	return (
		<Routes>
			<Route path="/login" element={<Login />} />

			<Route path="/" element={
				<RequireAdmin>
					<AdminLayout />
				</RequireAdmin>
			}>
				<Route index element={<Dashboard />} />
				<Route path="users" element={<Users />} />
				<Route path="users/:id" element={<UserHistory />} />
				<Route path="feedback" element={<Feedback />} />
				<Route path="notice" element={<Notice />} />
			</Route>

			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
};

const MainApp = () => {
	// useUser hook works here
	// If we need interceptors:
	// const navigate = useNavigate();
	// useEffect(() => {
	//   setupInterceptors(navigate);
	// }, [navigate]);

	return <MainRoutes />;
}

const App = () => {
	return (
		<Router>
			<LanguageProvider>
				<PopupProvider>
					<AlertProvider>
						<UserProvider>
							<MainApp />
						</UserProvider>
					</AlertProvider>
				</PopupProvider>
			</LanguageProvider>
		</Router>
	);
};

export default App;